import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertOctagon,
  ArrowRight,
  Clock3,
  Inbox,
  PauseCircle,
  Settings2,
  Tag,
  Timer,
  ZapOff,
  BarChart3,
  CheckCircle2,
  Flame,
  KeyRound,
  Lightbulb,
  LifeBuoy,
  MailWarning,
  Plug,
  PlugZap,
  Unplug,
  Webhook,
} from "lucide-react";
import { useAppServices } from "../../hook/services";
import { cx } from "../../utils";
import AppIcon from "../../components/ui/AppIcon";
import Skeleton from "../../components/ui/Skeleton";
import { useToast } from "../../components/ui/Toast";
import { ago } from "../../components/directory/shared";

/**
 * Home: what needs acting on today. Each card counts one kind of problem,
 * shows up to five, and links to the full filtered list. Groups with nothing
 * in them drop to an "all clear" line at the bottom.
 *
 * Data: GET /directory/attention (backend routes/directory/services.js).
 */

const TONE = {
  rose: { ring: "border-rose-200/80", icon: "bg-rose-50 text-rose-600", pill: "bg-rose-600 text-white" },
  amber: { ring: "border-amber-200/80", icon: "bg-amber-50 text-amber-600", pill: "bg-amber-500 text-white" },
  brand: { ring: "border-brand-200/80", icon: "bg-brand-50 text-brand-600", pill: "bg-brand-600 text-white" },
  violet: { ring: "border-violet-200/80", icon: "bg-violet-50 text-violet-600", pill: "bg-violet-600 text-white" },
  ink: { ring: "border-ink-200/80", icon: "bg-ink-100 text-ink-500", pill: "bg-ink-700 text-white" },
};

const installTo = (i) => `/installs?q=${encodeURIComponent(i.locationId || i.account || "")}`;

const AppLine = ({ item, detail }) => (
  <>
    <AppIcon app={item} size="sm" className="!h-7 !w-7 !rounded-md !text-[9px]" />
    <span className="min-w-0 flex-1">
      <span className="block truncate text-[13px] font-medium text-ink-800">{item.account}</span>
      <span className="block truncate text-[11.5px] text-ink-400">{item.appName}</span>
    </span>
    {detail && <span className="shrink-0 text-[11.5px] text-ink-400">{detail}</span>}
  </>
);

const TicketLine = ({ t }) => (
  <>
    <span className="shrink-0 font-mono text-[11px] text-ink-400">#{t.number}</span>
    <span className="min-w-0 flex-1">
      <span className="block truncate text-[13px] font-medium text-ink-800">{t.subject}</span>
      <span className="block truncate text-[11.5px] text-ink-400">{[t.who, t.appName].filter(Boolean).join(" · ")}</span>
    </span>
    {(t.priority === "urgent" || t.priority === "high") && (
      <span className={cx("shrink-0 rounded-full px-2 py-0.5 text-[10.5px] font-semibold", t.priority === "urgent" ? "bg-rose-50 text-rose-600" : "bg-amber-50 text-amber-700")}>
        {t.priority === "urgent" ? "Urgent" : "High"}
      </span>
    )}
    <span className="shrink-0 text-[11.5px] text-ink-400">{ago(t.at)}</span>
  </>
);

// What each group means. ORDER below sets how they rank on the page.
const GROUP_LIST = [
  {
    id: "broken",
    tone: "rose",
    icon: Unplug,
    title: "Installs that stopped working",
    why: "Installed, but we hold no access token — the app can't act for these accounts until they reinstall.",
    clear: "Every install has a working token",
    row: (i) => ({ to: installTo(i), body: <AppLine item={i} detail={ago(i.updatedAt)} /> }),
  },
  {
    id: "errorSpikes",
    tone: "rose",
    icon: Flame,
    title: "Error spikes",
    why: "Accounts failing far more in the last 24 hours than their usual.",
    clear: "No unusual errors in the last 24 hours",
    row: (s) => ({
      to: installTo(s),
      body: (
        <>
          <AppIcon app={s} size="sm" className="!h-7 !w-7 !rounded-md !text-[9px]" />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13px] font-medium text-ink-800">{s.account}</span>
            <span className="block truncate text-[11.5px] text-rose-600" title={s.lastError}>{s.lastError || s.appName}</span>
          </span>
          <span className="shrink-0 text-right text-[11.5px] tabular-nums">
            <span className="block font-semibold text-rose-600">{s.today} today</span>
            <span className="block text-ink-400">usually {s.dailyAverage}/day</span>
          </span>
        </>
      ),
    }),
  },
  {
    id: "urgentTickets",
    tone: "amber",
    icon: AlertOctagon,
    title: "Urgent and high-priority tickets",
    why: "Still open or waiting on the customer.",
    clear: "No urgent tickets open",
    row: (t) => ({ to: `/support?status=all&q=${encodeURIComponent(`#${t.number}`)}`, body: <TicketLine t={t} /> }),
  },
  {
    id: "unreadTickets",
    tone: "brand",
    icon: LifeBuoy,
    title: "Unread support messages",
    why: "Customers waiting for a reply.",
    clear: "No unread support messages",
    row: (t) => ({ to: `/support?status=all&q=${encodeURIComponent(`#${t.number}`)}`, body: <TicketLine t={t} /> }),
  },
  {
    id: "noCredentials",
    tone: "amber",
    icon: KeyRound,
    title: "Installed, but no credentials",
    why: "Nothing can be sent until the customer saves their provider details.",
    clear: "Every install has credentials saved",
    row: (i) => ({ to: installTo(i), body: <AppLine item={i} detail={`installed ${ago(i.createdAt)}`} /> }),
  },
  {
    id: "waitingForMessage",
    tone: "amber",
    icon: Webhook,
    title: "No message received yet",
    why: "Credentials saved 2+ days ago, but no reply has come in — the webhook may not be set up at the provider.",
    clear: "Every set-up install is receiving",
    row: (i) => ({ to: installTo(i), body: <AppLine item={i} detail={`installed ${ago(i.createdAt)}`} /> }),
  },
  {
    id: "newRequests",
    tone: "violet",
    icon: Lightbulb,
    title: "New feature requests",
    why: "Not reviewed yet.",
    clear: "No feature requests waiting",
    row: (r) => ({
      to: `/feature-requests?q=${encodeURIComponent(r.title)}`,
      body: (
        <>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13px] font-medium text-ink-800">{r.title}</span>
            <span className="block truncate text-[11.5px] text-ink-400">{[r.locationName, r.appName].filter(Boolean).join(" · ")}</span>
          </span>
          <span className="shrink-0 text-[11.5px] text-ink-400">{ago(r.createdAt)}</span>
        </>
      ),
    }),
  },
  {
    id: "uninstalls",
    tone: "ink",
    icon: MailWarning,
    title: "Uninstalled this week",
    why: "Worth a look at why — and a message, if the account is known.",
    clear: "No uninstalls this week",
    row: (i) => ({ to: installTo(i), body: <AppLine item={i} detail={ago(i.updatedAt)} /> }),
  },
];

GROUP_LIST.push(
  {
    id: "inboundFailures",
    tone: "rose",
    icon: Inbox,
    title: "Replies failing to arrive",
    why: "Messages from contacts that couldn't be added to the CRM in the last 24 hours.",
    clear: "Every reply reached the CRM",
    row: (r) => ({
      to: installTo(r),
      body: (
        <>
          <AppIcon app={r} size="sm" className="!h-7 !w-7 !rounded-md !text-[9px]" />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13px] font-medium text-ink-800">{r.account}</span>
            <span className="block truncate text-[11.5px] text-rose-600" title={r.lastError}>{r.lastError || r.appName}</span>
          </span>
          <span className="shrink-0 text-[11.5px] font-semibold tabular-nums text-rose-600">{r.n} failed</span>
        </>
      ),
    }),
  },
  {
    id: "slowTickets",
    tone: "amber",
    icon: Clock3,
    title: "Tickets waiting over a day",
    why: "Open, and the customer has been waiting more than 24 hours for a reply.",
    clear: "No ticket waiting over a day",
    row: (t) => ({ to: `/support?status=all&q=${encodeURIComponent(`#${t.number}`)}`, body: <TicketLine t={t} /> }),
  },
  {
    id: "trialsEnding",
    tone: "violet",
    icon: Timer,
    title: "Trials ending soon",
    why: "Trials ending in the next 48 hours — make sure they're set up and sending before they decide.",
    clear: "No trials ending in the next 2 days",
    row: (i) => ({ to: installTo(i), body: <AppLine item={i} detail={`ends ${new Date(i.endsAt).toLocaleString(undefined, { weekday: "short", hour: "numeric", minute: "2-digit" })}`} /> }),
  },
  {
    id: "goneQuiet",
    tone: "amber",
    icon: ZapOff,
    title: "Gone quiet",
    why: "Were sending, but nothing for 7+ days — at risk of uninstalling.",
    clear: "No active account has gone quiet",
    row: (i) => ({ to: installTo(i), body: <AppLine item={i} detail={`last ${ago(i.lastActivityAt)}`} /> }),
  },
  {
    id: "staleWaiting",
    tone: "ink",
    icon: PauseCircle,
    title: "Waiting on the customer for days",
    why: "Marked waiting on the customer 3+ days ago — follow up or close.",
    clear: "No ticket stuck waiting on a customer",
    row: (t) => ({ to: `/support?status=all&q=${encodeURIComponent(`#${t.number}`)}`, body: <TicketLine t={t} /> }),
  },
  {
    id: "unnamedPlans",
    tone: "violet",
    icon: Tag,
    title: "Plans without a name or price",
    why: "Installs are arriving on these plan IDs, so they're missing from revenue.",
    clear: "Every plan in use is named and priced",
    row: (r) => ({
      to: r.appRef ? `/apps/${r.appRef}/edit` : "/apps",
      body: (
        <>
          <AppIcon app={r} size="sm" className="!h-7 !w-7 !rounded-md !text-[9px]" />
          <span className="min-w-0 flex-1">
            <span className="block truncate font-mono text-[12px] text-ink-800">{r.planId}</span>
            <span className="block truncate text-[11.5px] text-ink-400">{r.appName}</span>
          </span>
          <span className="shrink-0 text-[11.5px] text-ink-400">{r.n} install{r.n === 1 ? "" : "s"}</span>
        </>
      ),
    }),
  },
  {
    id: "appSetup",
    tone: "ink",
    icon: Settings2,
    title: "App setup gaps",
    why: "Your apps missing something installs rely on.",
    clear: "Every app is fully set up",
    row: (a) => ({
      to: `/apps/${a._id}/edit`,
      body: (
        <>
          <AppIcon app={a} size="sm" className="!h-7 !w-7 !rounded-md !text-[9px]" />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13px] font-medium text-ink-800">{a.appName}</span>
            <span className="block truncate text-[11.5px] text-amber-600">Missing {a.missing.join(", ")}</span>
          </span>
        </>
      ),
    }),
  },
);

// Most damaging first: broken sends and replies, then waiting customers, then
// revenue risk, then housekeeping.
const ORDER = [
  "broken",
  "errorSpikes",
  "inboundFailures",
  "urgentTickets",
  "slowTickets",
  "unreadTickets",
  "trialsEnding",
  "goneQuiet",
  "noCredentials",
  "waitingForMessage",
  "staleWaiting",
  "newRequests",
  "unnamedPlans",
  "appSetup",
  "uninstalls",
];
const GROUPS = ORDER.map((id) => GROUP_LIST.find((g) => g.id === id)).filter(Boolean);

const Headline = ({ icon: Icon, label, value, hint, to, alert }) => (
  <Link
    to={to}
    className={cx(
      "group rounded-2xl border bg-white px-4 py-3.5 shadow-card transition-colors hover:border-ink-300",
      alert ? "border-rose-200" : "border-ink-200/70",
    )}
  >
    <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-ink-500">
      <Icon size={12} className="text-ink-400" aria-hidden />
      {label}
    </p>
    <p className={cx("mt-1.5 text-[22px] font-semibold leading-none tabular-nums", alert ? "text-rose-600" : "text-ink-900")}>{value}</p>
    <p className="mt-1.5 truncate text-[11.5px] text-ink-400">{hint}</p>
  </Link>
);

const Dashboard = () => {
  const { directory } = useAppServices();
  const toast = useToast();
  const [data, setData] = useState(null);

  useEffect(() => {
    directory.Attention().then(({ response, error }) => {
      if (error) toast(error?.message || error?.error || "Could not load what needs attention", "error");
      setData(response || { headline: {}, groups: {} });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!data) {
    return (
      <div className="space-y-5">
        <Skeleton className="h-7 w-64" />
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {[0, 1, 2, 3].map((k) => (
            <Skeleton key={k} className="h-24 w-full" rounded="rounded-2xl" />
          ))}
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {[0, 1].map((k) => (
            <Skeleton key={k} className="h-56 w-full" rounded="rounded-2xl" />
          ))}
        </div>
      </div>
    );
  }

  const h = data.headline || {};
  const errorRate = h.runs24h ? (h.errors24h / h.runs24h) * 100 : 0;
  const open = GROUPS.filter((g) => data.groups?.[g.id]?.count > 0);
  const clear = GROUPS.filter((g) => !(data.groups?.[g.id]?.count > 0));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-[22px] font-semibold tracking-tight text-ink-900">
          {open.length ? `${open.length} thing${open.length === 1 ? "" : "s"} need${open.length === 1 ? "s" : ""} attention` : "All clear"}
        </h1>
        <p className="mt-1 text-[13.5px] text-ink-500">
          {open.length ? "Most urgent first. Each list links to the full, filtered view." : "Nothing needs you right now — installs, support and errors all look normal."}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Headline icon={Plug} label="Active installs" value={(h.activeInstalls || 0).toLocaleString()} hint="Across every app" to="/installs?status=active" />
        <Headline icon={PlugZap} label="New this week" value={(h.newThisWeek || 0).toLocaleString()} hint="Installed in the last 7 days" to="/installs?sort=newest" />
        <Headline
          icon={Flame}
          label="Errors · 24h"
          value={(h.errors24h || 0).toLocaleString()}
          hint={`${errorRate.toFixed(errorRate < 10 ? 1 : 0)}% of ${(h.runs24h || 0).toLocaleString()} runs`}
          to="/analytics?days=7"
          alert={errorRate >= 5}
        />
        <Headline icon={LifeBuoy} label="Unread tickets" value={(h.unreadTickets || 0).toLocaleString()} hint="Waiting for a reply" to="/support?status=all&unread=1" alert={h.unreadTickets > 0} />
      </div>

      {open.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 [&>*]:min-w-0">
          {open.map((g) => {
            const group = data.groups[g.id];
            const t = TONE[g.tone];
            const Icon = g.icon;
            return (
              <section key={g.id} className={cx("flex flex-col overflow-hidden rounded-2xl border bg-white shadow-card", t.ring)}>
                <header className="flex items-start gap-3 px-5 pb-3 pt-4">
                  <span className={cx("grid h-9 w-9 shrink-0 place-items-center rounded-xl", t.icon)}>
                    <Icon size={17} aria-hidden />
                  </span>
                  <div className="min-w-0 flex-1">
                    <h2 className="flex items-center gap-2 text-[14px] font-semibold text-ink-900">
                      {g.title}
                      <span className={cx("rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums", t.pill)}>{group.count}</span>
                    </h2>
                    <p className="mt-0.5 text-[12.5px] leading-relaxed text-ink-500">{g.why}</p>
                  </div>
                </header>
                <ul className="flex-1 divide-y divide-ink-150 border-t border-ink-150">
                  {group.items.map((item, k) => {
                    const { to, body } = g.row(item);
                    return (
                      <li key={item._id || item.locationId || k}>
                        <Link to={to} className="flex items-center gap-3 px-5 py-2.5 transition-colors hover:bg-ink-50">
                          {body}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
                <Link to={group.link} className="flex items-center justify-between border-t border-ink-150 bg-ink-50/60 px-5 py-2.5 text-[12.5px] font-medium text-ink-600 hover:bg-ink-100 hover:text-ink-900">
                  {/* The full list can differ a little (it has no 2-day grace, for one), so no count is promised. */}
                  {group.count > group.items.length ? `${group.count - group.items.length} more — open the full list` : "Open the full list"}
                  <ArrowRight size={13} />
                </Link>
              </section>
            );
          })}
        </div>
      )}

      {clear.length > 0 && (
        <div className="rounded-2xl border border-ink-200/70 bg-white px-5 py-4 shadow-card">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-500">All clear</p>
          <ul className="mt-2.5 grid gap-x-6 gap-y-2 sm:grid-cols-2">
            {clear.map((g) => (
              <li key={g.id} className="flex items-center gap-2 text-[13px] text-ink-600">
                <CheckCircle2 size={14} className="shrink-0 text-emerald-500" aria-hidden />
                {g.clear}
              </li>
            ))}
          </ul>
        </div>
      )}

      <Link to="/analytics" className="inline-flex items-center gap-1.5 text-[13px] font-medium text-brand-600 hover:underline">
        <BarChart3 size={14} /> See trends in Analytics
      </Link>
    </div>
  );
};

export default Dashboard;
